'use client';

import { ReactNode, useEffect, useState } from 'react';

import { cn } from '@libs/utils';

export type ToastType = 'success' | 'danger' | 'warning' | 'info';

export interface ToastConfig {
  type: ToastType;
  title?: string;
  message: string;
  duration?: number; // milliseconds, nếu không có thì không tự tắt
  showCloseButton: boolean;
  position:
    | 'top-right'
    | 'top-left'
    | 'bottom-right'
    | 'bottom-left'
    | 'top-center'
    | 'bottom-center';
  animation: 'slide-in' | 'fade' | 'bounce';
}

export interface ToastState {
  isVisible: boolean;
  config: ToastConfig;
  timeoutId?: ReturnType<typeof setTimeout>;
}

export interface ToastProps {
  id: string;
  type: ToastType;
  title?: string;
  message: string;
  isVisible: boolean;
  duration?: number;
  showCloseButton?: boolean;
  onClose: () => void;
  className?: string;
}

export interface ToastContextType {
  toasts: Record<string, ToastState>;
  showToast: (
    type: ToastType,
    message: string | React.ReactNode,
    title?: string,
    duration?: number
  ) => string;
  hideToast: (id: string) => void;
  clearAllToasts: () => void;
}

export interface ToastProviderProps {
  children: ReactNode;
}

export const Toast = ({
  id,
  type,
  title,
  message,
  isVisible,
  duration,
  showCloseButton = true,
  onClose,
  className,
}: ToastProps) => {
  const [isShowing, setIsShowing] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [remaining, setRemaining] = useState<number | undefined>(duration);
  const [startAt, setStartAt] = useState<number | null>(null);

  useEffect(() => {
    if (isVisible) {
      // Delay để trigger animation
      const timer = setTimeout(() => setIsShowing(true), 10);
      return () => clearTimeout(timer);
    } else {
      setIsShowing(false);
    }
  }, [isVisible]);

  // Tự đóng theo từng toast, hỗ trợ pause on hover
  useEffect(() => {
    if (!duration || duration <= 0) return;
    if (!isVisible) return;

    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let rafId: number | undefined;

    const start = () => {
      setStartAt(Date.now());
      timeoutId = setTimeout(() => {
        onClose();
      }, remaining ?? duration);
    };

    const pause = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = undefined;
      }
      if (startAt != null) {
        const elapsed = Date.now() - startAt;
        setRemaining(prev => (prev != null ? Math.max(prev - elapsed, 0) : undefined));
      }
    };

    if (!hovered) {
      start();
    } else {
      pause();
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (rafId) window.cancelAnimationFrame(rafId);
    };
  }, [duration, hovered, isVisible]);

  const getToastStyles = (type: ToastType) => {
    const baseStyles =
      'flex items-start gap-4 rounded-lg shadow-lg border backdrop-blur-sm transition-all duration-300 ease-in-out transform w-full max-w-[580px]';

    const padding = 'px-[14px] py-[17px]';

    switch (type) {
      case 'success':
        return `${baseStyles} ${padding} bg-[#E7F2FF] border-[rgba(19,121,240,0.2)] text-[#27314B]`;
      case 'danger':
        return `${baseStyles} ${padding} bg-[#FCEEED] border-[rgba(237,20,59,0.2)] text-[#27314B]`;
      case 'warning':
        return `${baseStyles} ${padding} bg-yellow-50 border-yellow-200 text-yellow-800`;
      case 'info':
        return `${baseStyles} ${padding} bg-blue-50 border-blue-200 text-blue-800`;
      default:
        return `${baseStyles} ${padding} bg-gray-50 border-gray-200 text-gray-800`;
    }
  };

  const getIconContainerStyles = (type: ToastType) => {
    const baseStyles = 'flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center';

    switch (type) {
      case 'success':
        return `${baseStyles}`;
      case 'danger':
        return `${baseStyles}`;
      case 'warning':
        return `${baseStyles}`;
      case 'info':
        return `${baseStyles}`;
      default:
        return `${baseStyles}`;
    }
  };

  const getIconStyles = (type: ToastType) => {
    const baseIconStyles = 'w-6 h-6';

    switch (type) {
      case 'success':
        return `${baseIconStyles} text-[#1379F0]`;
      case 'danger':
        return `${baseIconStyles} text-[#ED143B]`;
      case 'warning':
        return `${baseIconStyles} text-yellow-500`;
      case 'info':
        return `${baseIconStyles} text-blue-500`;
      default:
        return `${baseIconStyles} text-gray-500`;
    }
  };

  const getIcon = (type: ToastType) => {
    switch (type) {
      case 'success':
        return (
          <div className={getIconContainerStyles(type)}>
            <svg className={getIconStyles(type)} fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        );
      case 'danger':
        return (
          <div className={getIconContainerStyles(type)}>
            <svg
              className="w-6 h-6"
              viewBox="0 0 46 46"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                opacity="0.1"
                d="M22.9625 43.4374C21.4828 43.4414 20.0285 43.0547 18.7477 42.3169L8.25826 36.2964C6.98382 35.5571 5.92596 34.4983 5.18986 33.2255C4.45377 31.9528 4.0651 30.5103 4.0625 29.0415V16.9626C4.0651 15.4938 4.45377 14.0513 5.18986 12.7785C5.92596 11.5057 6.98382 10.447 8.25826 9.70761L18.7477 3.68715C20.0291 2.95038 21.4828 2.5625 22.9625 2.5625C24.4422 2.5625 25.8959 2.95038 27.1773 3.68715L37.6667 9.70761C38.9412 10.447 39.999 11.5057 40.7351 12.7785C41.4712 14.0513 41.8599 15.4938 41.8625 16.9626V29.0415C41.8599 30.5103 41.4712 31.9528 40.7351 33.2255C39.999 34.4983 38.9412 35.5571 37.6667 36.2964L27.1773 42.3169C25.8976 43.0573 24.4426 43.4441 22.9625 43.4374Z"
                fill="#ED143B"
              />
              <path
                d="M23 2.375C24.4871 2.375 25.9485 2.76624 27.2363 3.50977L37.7773 9.58594C39.0581 10.3321 40.1216 11.4001 40.8613 12.6846C41.6011 13.969 41.9915 15.425 41.9941 16.9072V29.0967C41.9915 30.5789 41.601 32.0349 40.8613 33.3193C40.1216 34.6037 39.058 35.6718 37.7773 36.418L27.2363 42.4941C25.9502 43.2413 24.4874 43.6317 23 43.625C21.5131 43.6289 20.0517 43.2387 18.7646 42.4941L8.22266 36.418C6.94214 35.6719 5.8793 34.6036 5.13965 33.3193C4.3999 32.0349 4.00944 30.5789 4.00684 29.0967V16.9072C4.00944 15.425 4.3999 13.969 5.13965 12.6846C5.87931 11.4003 6.94214 10.332 8.22266 9.58594L18.7646 3.50977C20.0524 2.76635 21.5131 2.37504 23 2.375ZM23 5.29199C22.0206 5.29248 21.0575 5.54346 20.2021 6.02051L9.58398 12.0771C8.75349 12.5789 8.06753 13.288 7.59375 14.1348C7.11998 14.9815 6.87496 15.937 6.88184 16.9072V29.0967C6.87496 30.0669 7.12002 31.0224 7.59375 31.8691C8.06753 32.7159 8.75349 33.425 9.58398 33.9268L20.125 40.0215C20.9806 40.4981 21.9444 40.748 22.9238 40.748C23.9032 40.748 24.8661 40.4981 25.7217 40.0215L36.417 33.9268C37.2616 33.4346 37.9627 32.7291 38.4502 31.8818C38.9376 31.0345 39.1943 30.0742 39.1953 29.0967V16.9072C39.1943 15.9297 38.9377 14.9694 38.4502 14.1221C37.9626 13.2748 37.2616 12.5693 36.417 12.0771L25.875 5.98242C24.9969 5.49247 24.0055 5.2411 23 5.25391V5.29199ZM23 28.292C24.3231 28.2921 25.3955 29.3644 25.3955 30.6875C25.3955 32.0106 24.3231 33.0829 23 33.083C21.6768 33.083 20.6045 32.0107 20.6045 30.6875C20.6045 29.3644 21.6769 28.292 23 28.292ZM23 12.9199C23.5083 12.9199 23.996 13.122 24.3555 13.4814C24.7148 13.8409 24.917 14.3287 24.917 14.8369V23.7686C24.9169 24.2766 24.7146 24.7637 24.3555 25.123C23.996 25.4825 23.5083 25.6846 23 25.6846C22.4917 25.6846 22.004 25.4825 21.6445 25.123C21.2854 24.7637 21.0831 24.2766 21.083 23.7686V14.8369C21.083 14.3286 21.2852 13.8409 21.6445 13.4814C22.004 13.122 22.4917 12.9199 23 12.9199Z"
                fill="#ED143B"
              />
            </svg>
          </div>
        );
      case 'warning':
        return (
          <div className={getIconContainerStyles(type)}>
            <svg className={getIconStyles(type)} fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        );
      case 'info':
        return (
          <div className={getIconContainerStyles(type)}>
            <svg className={getIconStyles(type)} fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        );
      default:
        return null;
    }
  };

  const animationClasses = isShowing ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0';

  if (!isVisible && !isShowing) return null;

  return (
    <div
      className={cn(getToastStyles(type), animationClasses, className, 'flex items-center')}
      role="alert"
      aria-live="polite"
      data-toast-id={id}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {getIcon(type)}

      <div className="flex-1 min-w-0 flex flex-col justify-center gap-1">
        {title && (
          <div className="font-semibold text-[15px] leading-[16px] text-[#27314B]">{title}</div>
        )}
        <div className="font-medium text-[13px] leading-[20px] text-[#4B5675]">{message}</div>
      </div>

      {showCloseButton && (
        <button
          type="button"
          className="flex-shrink-0 p-1 rounded-md hover:bg-black hover:bg-opacity-10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-current transition-colors"
          onClick={onClose}
          aria-label="Đóng thông báo"
        >
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      )}
    </div>
  );
};
